import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { UnifiedReaderLayout } from './index';
import type {
  NavigationState,
  NavigationCallbacks,
  ReaderActions,
  BookMetadata,
  TOCItem,
  Bookmark,
  Note,
  SearchResult,
  HighlightColor,
} from './types';
import { BookFormat } from './types';
import LoadingSpinner from '../LoadingSpinner';
import { useBookMetadata } from '../../hooks/useBooks';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useHighlights } from '../../hooks/useHighlights';
import { useFlashcardDecks, useFlashcardsInDeck } from '../../hooks/useFlashcards';
import { useReadingProgress } from '../../hooks/useReadingProgress';
import { SecureReadingProvider, useSecureReading } from '../../contexts/SecureReadingContext';
import { useDrmProtection } from '../../hooks/useDrmProtection';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import StudyToolsFAB from './StudyToolsFAB';
import TextSelectionMenuWithFlashcard from './TextSelectionMenuWithFlashcard';
import HighlightNoteModal from './HighlightNoteModal';
import { getTransparentHighlightColor } from '../../utils/colorUtils';
import { useAlert } from '../../contexts/AlertContext';
import { useActiveChunkIdTracker, makeChunkKey } from './useActiveChunkIdTracker';
import { pdfChunkService } from '../../api/pdfChunkService';

// Configure PDF.js worker (served locally from /public to avoid CORS issues)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Walk a PDF.js outline tree into our shared TOCItem[] shape.
 *
 * Each outline item's `dest` may be:
 *   - an array (explicit destination: `[pageRef, mode, ...]`)
 *   - a string (named destination — must be resolved via `getDestination`)
 *   - null (e.g., a grouping header with no page jump)
 *
 * `pageIndex` is stored 0-based so it lines up with the TOC panel's "p. N+1"
 * label. The PDF nav handler adds +1 before calling `setPageNumber`.
 */
async function extractPdfOutline(pdf: any): Promise<TOCItem[]> {
  const outline = await pdf.getOutline();
  console.log('[PDF TOC] getOutline() →', outline);
  if (!outline || outline.length === 0) return [];

  const resolvePageIndex = async (dest: any): Promise<number | undefined> => {
    try {
      const explicit = Array.isArray(dest)
        ? dest
        : typeof dest === 'string'
        ? await pdf.getDestination(dest)
        : null;
      if (!explicit || !explicit[0]) return undefined;
      const pageIndex = await pdf.getPageIndex(explicit[0]);
      return typeof pageIndex === 'number' ? pageIndex : undefined;
    } catch (err) {
      console.warn('[PDF TOC] resolvePageIndex failed for dest', dest, err);
      return undefined;
    }
  };

  const walk = async (items: any[], level: number, idPath: string): Promise<TOCItem[]> => {
    const out: TOCItem[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const id = `${idPath}${i}`;
      const pageIndex = await resolvePageIndex(item.dest);
      const children =
        item.items && item.items.length > 0 ? await walk(item.items, level + 1, `${id}-`) : undefined;
      out.push({
        id,
        label: (item.title || '').trim() || '(untitled)',
        pageNumber: pageIndex,
        level,
        children,
      });
    }
    return out;
  };

  const toc = await walk(outline, 0, 'pdf-toc-');
  console.log(`[PDF TOC] built ${toc.length} top-level items`, toc);
  return toc;
}

interface UnifiedPdfRendererProps {
  bookId: string;
  isDrmEnabled?: boolean;
}

/**
 * Internal component that contains the actual reader logic
 */
function UnifiedPdfRendererInternal({ bookId, isDrmEnabled = false }: UnifiedPdfRendererProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useAlert();

  // DRM session management (only if DRM is enabled)
  const drmContext = isDrmEnabled ? useSecureReading() : null;

  // Apply DRM protections when DRM is enabled
  // NOTE: We allow text selection for highlights/notes, but block copying via keyboard shortcuts
  useDrmProtection({
    disableRightClick: isDrmEnabled,
    disableTextSelection: false, // ✅ Allow selection for highlights/notes (copying blocked via Ctrl+C)
    disableKeyboardShortcuts: isDrmEnabled, // ✅ Blocks Ctrl+C, Ctrl+P, Ctrl+S
    detectDevTools: isDrmEnabled,
    disablePrint: isDrmEnabled,
    onDevToolsDetected: isDrmEnabled ? () => {
      console.error('DevTools detected! Navigating away...');
      navigate('/');
    } : undefined,
    onViolationAttempt: isDrmEnabled ? (type) => {
      console.warn(`DRM violation attempt: ${type}`);
    } : undefined,
  });

  // Initialize DRM session if enabled
  useEffect(() => {
    if (isDrmEnabled && drmContext && !drmContext.session) {
      drmContext.initiateSession(bookId).catch((err) => {
        console.error('Failed to initiate DRM session:', err);
      });
    }
  }, [isDrmEnabled, bookId, drmContext]);
  // Current page state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);

  // Flashcard Library support: current page's SecureContentChunkId (Guid).
  // At book-open time we fetch /pdf-chunks/metadata (which now carries
  // `secureContentChunkId` per chunk) and harvest every page in each chunk's
  // [startPage, endPage] range, pointing every page key at the same chunk
  // Guid. This lets the Library panel resolve the active chunk no matter
  // which page within the chunk range is currently visible. The tracker also
  // still harvests opportunistically from any /search-in-book responses that
  // flow through (future-proofing for backend-backed PDF search).
  const chunkIdTracker = useActiveChunkIdTracker();
  const activeSecureContentChunkId = useMemo(
    () => chunkIdTracker.resolveForKey(makeChunkKey('page', pageNumber)),
    [pageNumber, chunkIdTracker.mapping, chunkIdTracker.resolveForKey]
  );

  // Pre-populate the tracker from the PDF chunk metadata endpoint. Fires
  // once per bookId; a failure here is non-fatal (the Library tab just
  // shows its empty state until a search is run).
  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    pdfChunkService
      .getMetadata(bookId)
      .then((metadata) => {
        if (cancelled) return;
        // Expand each chunk's [startPage, endPage] range into one tracker
        // entry per page, so any page within a chunk's range resolves to
        // the same secureContentChunkId.
        const perPage: { pageNumber: number; secureContentChunkId: string }[] = [];
        for (const chunk of metadata.chunks ?? []) {
          if (!chunk.secureContentChunkId) continue;
          const from = chunk.startPage;
          const to = chunk.endPage;
          if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
          for (let p = from; p <= to; p++) {
            perPage.push({ pageNumber: p, secureContentChunkId: chunk.secureContentChunkId });
          }
        }
        chunkIdTracker.harvestChapterList(
          perPage,
          (entry) => makeChunkKey('page', entry.pageNumber)
        );
      })
      .catch((err) => {
        console.warn('[UnifiedPdfRenderer] pdfChunkService.getMetadata failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId, chunkIdTracker.harvestChapterList]);

  // Search-highlight state. Set when a user clicks a search result so the
  // current page's text layer gets <mark>-wrapped matches and the first hit
  // scrolls into view.
  const [searchHighlightQuery, setSearchHighlightQuery] = useState<string>('');
  const lastSearchQueryRef = useRef<string>('');
  // Scroll-to-hit is only desired right after the user clicks a search
  // result. Other text-layer re-renders (zoom, resize) must not scroll.
  const pendingSearchScrollRef = useRef<boolean>(false);
  // Timer that clears the search highlight after a short delay so the
  // yellow marks don't linger indefinitely after the user jumps to a hit.
  const highlightFadeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (highlightFadeTimerRef.current !== null) {
        window.clearTimeout(highlightFadeTimerRef.current);
      }
    };
  }, []);

  // Zoom model: 100% = "fit to viewport height" (fills available height).
  // userZoom is a multiplier on top of the fit — so the PDF always fills the
  // available space at 100% regardless of viewport size, and zoom in/out scales
  // relative to that baseline.
  const ZOOM_MIN = 0.2;
  const ZOOM_MAX = 5;
  const ZOOM_STEP = 0.2;

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null); // PDFDocumentProxy for text-content search
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [userZoom, setUserZoom] = useState<number>(1); // 1.0 == 100% (fit)

  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      // Fallback to parent or viewport when the element hasn't settled yet.
      const width = rect.width || el.parentElement?.getBoundingClientRect().width || window.innerWidth;
      const height = rect.height || el.parentElement?.getBoundingClientRect().height || window.innerHeight;
      setContainerSize({ width, height });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Size the page so it fills the available height at 100%, then scale by userZoom.
  // We pass height to <Page>; react-pdf auto-computes scale while keeping aspect ratio.
  const pagePixelHeight = useMemo(() => {
    const padding = 32; // outer p-4 on both axes
    const availH = Math.max(0, containerSize.height - padding);
    if (availH <= 0) {
      // Fallback to a sensible default tied to viewport so we never render at tiny 0-size.
      return Math.max(400, window.innerHeight - 200) * userZoom;
    }
    return availH * userZoom;
  }, [containerSize.height, userZoom]);

  const handleZoomIn = useCallback(() => {
    setUserZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setUserZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  }, []);

  const handleResetZoom = useCallback(() => {
    setUserZoom(1);
  }, []);

  // Fetch book metadata
  const { data: metadata, isLoading } = useBookMetadata(bookId);

  // Memoize PDF file config to prevent recreating on every render
  // This prevents excessive HTTP requests
  const pdfFileConfig = useMemo(() => ({
    url: `/api/app/books-extn/stream-pdf/${bookId}`,
    withCredentials: true,
  }), [bookId]);

  // Memoize PDF.js options
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  }), []);

  // Handle document load success. `pdf` is a PDFDocumentProxy — keep a ref to
  // it so the search handler can extract text from every page on demand.
  const onDocumentLoadSuccess = useCallback((pdf: any) => {
    pdfDocRef.current = pdf;
    setNumPages(pdf.numPages);
    console.log(`PDF loaded: ${pdf.numPages} pages`);
    // Extract the outline in the background. Errors are non-fatal — the
    // TOC panel simply shows an "empty" state.
    extractPdfOutline(pdf)
      .then((toc) => setTableOfContents(toc))
      .catch((err) => console.warn('Failed to extract PDF outline:', err));
  }, []);

  // Build a text renderer that wraps matches with <mark> so the active search
  // query is visually highlighted in the PDF text layer. `customTextRenderer`
  // returns a string used as innerHTML by react-pdf, so we escape HTML and
  // inline the highlight style to avoid CSS coupling.
  const customTextRenderer = useMemo(() => {
    const query = searchHighlightQuery.trim();
    if (!query) return undefined;
    const needle = query.toLowerCase();
    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    return ({ str }: { str: string }) => {
      if (!str) return '';
      const lower = str.toLowerCase();
      let cursor = 0;
      let out = '';
      while (cursor < str.length) {
        const at = lower.indexOf(needle, cursor);
        if (at === -1) {
          out += escapeHtml(str.slice(cursor));
          break;
        }
        out += escapeHtml(str.slice(cursor, at));
        const matched = str.slice(at, at + needle.length);
        // Semi-transparent yellow so the canvas-rendered text below the
        // (transparent-colored) text layer remains readable. mix-blend-mode
        // is unreliable here because the text layer is its own stacking
        // context.
        out +=
          '<mark data-pdf-search-hit="1" style="background:rgba(253,224,71,0.55);color:inherit;padding:0;border-radius:2px;">' +
          escapeHtml(matched) +
          '</mark>';
        cursor = at + needle.length;
      }
      return out;
    };
  }, [searchHighlightQuery]);

  // After the text layer finishes rendering for the current page, scroll the
  // first highlighted match into view — but only when a search-result click
  // is pending, so zoom/resize-driven re-renders don't yank the scroll.
  const handleTextLayerRendered = useCallback(() => {
    if (!pendingSearchScrollRef.current || !searchHighlightQuery) return;
    pendingSearchScrollRef.current = false;
    const container = pdfContainerRef.current;
    if (!container) return;
    const first = container.querySelector<HTMLElement>('[data-pdf-search-hit="1"]');
    if (first) {
      first.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [searchHighlightQuery]);

  // Fetch bookmarks
  const {
    bookmarks,
    createBookmark,
    deleteBookmark,
    updateBookmark,
  } = useBookmarks(bookId);

  // Fetch highlights and notes
  const {
    highlights: notes,
    createHighlight,
    deleteHighlight,
    updateHighlight,
    createPageNote,
    isCreatingPageNote,
  } = useHighlights(bookId);

  // Flashcard decks for this book
  const { decks, createDeck } = useFlashcardDecks(bookId);
  const [currentDeckId, setCurrentDeckId] = useState<string | null>(null);
  const { createFlashcard } = useFlashcardsInDeck(currentDeckId);

  // Reading progress tracking with auto-save (every 30 seconds)
  const { progress: readingProgress, startAutoSave, stopAutoSave, saveProgress, saveProgressOnPageChange } = useReadingProgress(bookId, 30);

  // Text selection state for PDF text layer
  const [selectedText, setSelectedText] = useState<string>('');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectionPageNumber, setSelectionPageNumber] = useState<number>(1); // Capture page at selection time

  // Highlight note modal state
  const [clickedHighlight, setClickedHighlight] = useState<Note | null>(null);

  // Ref to the PDF text layer container (will be accessed from ChunkedPdfRenderer)
  const pdfTextLayerRef = useState<HTMLDivElement | null>(null)[0];

  // Table of contents extracted from the PDF outline (populated after the
  // document loads — see the effect below).
  const [tableOfContents, setTableOfContents] = useState<TOCItem[]>([]);

  // Reset page to 1 when bookId changes
  const restoredBookIdRef = useRef<string | null>(null);
  useEffect(() => {
    setPageNumber(1);
    setNumPages(0);
    restoredBookIdRef.current = null;
  }, [bookId]);

  // Restore saved reading progress ONCE per book.
  // Guarded so later cache updates from save mutations don't override the user's current page.
  useEffect(() => {
    if (restoredBookIdRef.current === bookId) return;
    if (!readingProgress || numPages <= 0) return;

    restoredBookIdRef.current = bookId;

    const savedPage = parseInt(readingProgress.currentLocation, 10);
    if (Number.isFinite(savedPage) && savedPage > 0 && savedPage <= numPages) {
      console.log(`📖 Restoring saved reading progress: page ${savedPage}`);
      setPageNumber(savedPage);
    }
  }, [readingProgress, numPages, bookId]);

  // Refs hold the latest pageNumber/numPages so the auto-save interval and unmount
  // cleanup always read fresh values. Deliberately kept out of the auto-save effect's deps
  // to avoid restarting the 30s interval on every page turn.
  const pageNumberRef = useRef(pageNumber);
  const numPagesRef = useRef(numPages);
  useEffect(() => { pageNumberRef.current = pageNumber; }, [pageNumber]);
  useEffect(() => { numPagesRef.current = numPages; }, [numPages]);

  // Start auto-save when PDF is loaded (only once when numPages is set)
  useEffect(() => {
    if (numPages > 0 && user) {
      console.debug('Starting reading progress auto-save for PDF');

      const cleanup = startAutoSave(
        () => pageNumberRef.current,
        () => `Page ${pageNumberRef.current}`,
        () => numPagesRef.current ? (pageNumberRef.current / numPagesRef.current) * 100 : 0
      );

      // Save progress when unmounting
      return () => {
        cleanup();
        const finalPage = pageNumberRef.current;
        const finalTotal = numPagesRef.current;
        saveProgress(
          finalPage,
          `Page ${finalPage}`,
          finalTotal ? (finalPage / finalTotal) * 100 : 0
        ).catch(err => console.error('Failed to save final reading progress:', err));
      };
    }
  }, [numPages, user]); // Only depend on numPages and user, NOT pageNumber

  // Save progress immediately when page changes
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip on initial mount (we just loaded the saved progress)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (numPages > 0 && user && pageNumber > 0) {
      saveProgressOnPageChange(
        pageNumber,
        `Page ${pageNumber}`,
        (pageNumber / numPages) * 100
      );
    }
  }, [pageNumber]); // Only depend on pageNumber, not the function

  // Convert PDF state to unified navigation state
  // NOTE: currentLocation is 0-based for consistency with EPUB renderer (toolbar adds +1 for display)
  const navigation: NavigationState = useMemo(
    () => ({
      currentLocation: pageNumber - 1, // Convert to 0-based for toolbar
      currentLocationLabel: `Page ${pageNumber}`,
      totalLocations: numPages || 1,
      progressPercentage: numPages ? (pageNumber / numPages) * 100 : 0,
      canNavigatePrev: pageNumber > 1,
      canNavigateNext: pageNumber < numPages,
    }),
    [pageNumber, numPages]
  );

  // Navigation callbacks
  const navigationCallbacks: NavigationCallbacks = useMemo(
    () => ({
      onNavigatePrev: () => setPageNumber((prev) => Math.max(1, prev - 1)),
      onNavigateNext: () => setPageNumber((prev) => Math.min(numPages, prev + 1)),
      onNavigateToLocation: (location) => {
        // location is 0-based from toolbar, convert to 1-based page number
        const page = (typeof location === 'number' ? location : parseInt(String(location), 10)) + 1;
        if (page >= 1 && page <= numPages) {
          setPageNumber(page);
        }
      },
      onNavigateToTOC: (item) => {
        // TOCItem.pageNumber is 0-based (consistent with the TOC panel's
        // "p. N+1" label); react-pdf uses 1-based page numbers.
        if (typeof item.pageNumber === 'number') {
          setPageNumber(item.pageNumber + 1);
        }
      },
      onNavigateToBookmark: (bookmark) => {
        const page = typeof bookmark.location === 'number' ? bookmark.location : 1;
        setPageNumber(page);
      },
      onNavigateToNote: (note) => {
        let page = 1;

        if (typeof note.location === 'number') {
          page = note.location;
        } else if (typeof note.location === 'string') {
          page = parseInt(note.location, 10) || 1;
        }

        console.log('Navigating to PDF page:', page);
        setPageNumber(page);
      },
      onNavigateToSearchResult: (result) => {
        const page = typeof result.location === 'number' ? result.location : 1;
        pendingSearchScrollRef.current = true;
        setPageNumber(page);
        // Trigger visual highlight on the target page using the last
        // executed query (result.excerpt is the matched substring and works
        // too, but the raw query keeps highlighting consistent across hits).
        setSearchHighlightQuery(lastSearchQueryRef.current || result.excerpt || '');
        // Fallback: React bails on setState with an unchanged value, so
        // clicking a different result on the same page + same query would
        // not re-render the text layer and `onRenderTextLayerSuccess` would
        // never fire. Trigger a manual scroll after a short delay; the
        // render path will clear the pending flag first if it fires.
        setTimeout(() => {
          if (!pendingSearchScrollRef.current) return;
          pendingSearchScrollRef.current = false;
          const container = pdfContainerRef.current;
          const first = container?.querySelector<HTMLElement>('[data-pdf-search-hit="1"]');
          first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 150);
        // Auto-clear the highlight after 3s so the marks don't linger.
        // Reset the timer on each click so rapid clicks still get the full
        // 3-second display window.
        if (highlightFadeTimerRef.current !== null) {
          window.clearTimeout(highlightFadeTimerRef.current);
        }
        highlightFadeTimerRef.current = window.setTimeout(() => {
          setSearchHighlightQuery('');
          highlightFadeTimerRef.current = null;
        }, 3000);
      },
    }),
    [numPages]
  );


  // Reader actions
  const actions: ReaderActions = useMemo(
    () => ({
      onCreateBookmark: async (location: string | number, note?: string) => {
        // The sidebar passes `currentLocation` which is 0-indexed for PDFs
        // (see `currentLocation: pageNumber - 1` above). Convert back to the
        // 1-indexed page number so both the saved value and the displayed
        // label match what the user sees in the toolbar / reader.
        const page =
          typeof location === 'number' ? location + 1 : pageNumber;
        const locationLabel = `Page ${page}`;

        await createBookmark({
          location: page,
          locationLabel,
          note,
        });
      },
      onDeleteBookmark: async (bookmarkId: string) => {
        await deleteBookmark(bookmarkId);
      },
      onUpdateBookmark: async (bookmarkId: string, note: string) => {
        await updateBookmark({ id: bookmarkId, note });
      },
      onCreateNote: async (
        location: string | number,
        selectedText: string,
        color: HighlightColor,
        noteText?: string
      ) => {
        const page = typeof location === 'number' ? location : pageNumber;
        const locationLabel = `Page ${page}`;

        await createHighlight({
          location: page,
          locationLabel,
          selectedText,
          color,
          noteText,
        });
      },
      onDeleteNote: async (noteId: string) => {
        await deleteHighlight(noteId);
      },
      onUpdateNote: async (noteId: string, noteText: string) => {
        // Find the note to get its current color
        const note = notes.find(n => n.id === noteId);
        if (note) {
          await updateHighlight({
            id: noteId,
            color: note.color,
            noteText
          });
        }
      },
      onSearch: async (query: string): Promise<SearchResult[]> => {
        const trimmed = query.trim();
        const pdf = pdfDocRef.current;
        lastSearchQueryRef.current = trimmed;
        if (!trimmed || !pdf) return [];

        const MAX_RESULTS = 200;
        const CONTEXT_CHARS = 40;
        const needle = trimmed.toLowerCase();
        const results: SearchResult[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          try {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            // Join items with spaces; EOL items insert a newline so line breaks
            // don't fuse adjacent tokens.
            const pageText = textContent.items
              .map((item: any) => {
                if (typeof item.str !== 'string') return '';
                return item.hasEOL ? item.str + '\n' : item.str;
              })
              .join(' ');

            const haystack = pageText.toLowerCase();
            let from = 0;
            let hitIdx = 0;
            while (from < haystack.length) {
              const at = haystack.indexOf(needle, from);
              if (at === -1) break;

              const before = pageText
                .slice(Math.max(0, at - CONTEXT_CHARS), at)
                .replace(/\s+/g, ' ')
                .trim();
              const excerpt = pageText.slice(at, at + trimmed.length);
              const after = pageText
                .slice(at + trimmed.length, at + trimmed.length + CONTEXT_CHARS)
                .replace(/\s+/g, ' ')
                .trim();

              results.push({
                id: `p${pageNum}-${at}-${hitIdx}`,
                excerpt,
                location: pageNum,
                locationLabel: `Page ${pageNum}`,
                beforeContext: before,
                afterContext: after,
              });

              from = at + trimmed.length;
              hitIdx++;
              if (results.length >= MAX_RESULTS) break;
            }

            if (results.length >= MAX_RESULTS) break;
          } catch (err) {
            console.warn(`PDF search: failed to extract text from page ${pageNum}`, err);
          }
        }

        console.log(`PDF search "${trimmed}" → ${results.length} result(s)`);
        return results;
      },
    }),
    [pageNumber, createBookmark, deleteBookmark, updateBookmark, createHighlight, deleteHighlight, updateHighlight, notes]
  );

  // Handle adding page-level notes
  const handleAddPageNote = useCallback(
    async (noteText: string, color: HighlightColor) => {
      try {
        await createPageNote({
          location: pageNumber,
          locationLabel: `Page ${pageNumber}`,
          color,
          noteText,
        });
      } catch (error) {
        console.error('Failed to create page note:', error);
        showToast('Failed to create page note. Please try again.', { type: 'error' });
      }
    },
    [createPageNote, pageNumber]
  );

  // Handle text selection from PDF text layer with word boundary adjustment
  useEffect(() => {
    const handleSelectionChange = () => {
      const currentSelection = window.getSelection();
      if (!currentSelection || currentSelection.rangeCount === 0) {
        setSelectedText('');
        setSelection(null);
        setMenuPosition(null);
        return;
      }

      let text = currentSelection.toString();

      // Skip if no text selected
      if (!text || text.trim().length === 0) {
        setSelectedText('');
        setSelection(null);
        setMenuPosition(null);
        return;
      }

      // Get the current range
      const range = currentSelection.getRangeAt(0);

      // Expand selection to word boundaries
      const adjustedRange = expandToWordBoundaries(range);

      // Get the adjusted text
      const adjustedText = adjustedRange.toString().trim();

      if (adjustedText && adjustedText.length > 0) {
        console.log('✅ PDF TEXT SELECTED:', {
          originalText: text.substring(0, 40) + (text.length > 40 ? '...' : ''),
          adjustedText: adjustedText.substring(0, 80) + (adjustedText.length > 80 ? '...' : ''),
          currentPage: pageNumber,
          textLength: adjustedText.length,
          wasAdjusted: text.trim() !== adjustedText
        });

        // Update the selection to the adjusted range (visually snap to word boundaries)
        if (text.trim() !== adjustedText) {
          currentSelection.removeAllRanges();
          currentSelection.addRange(adjustedRange);
          console.log('🔧 Selection adjusted to word boundaries');
        }

        setSelectedText(adjustedText);
        setSelection(currentSelection);
        setSelectionPageNumber(pageNumber); // Capture page number at selection time
        console.log('✅ SELECTION PAGE NUMBER SET TO:', pageNumber);

        // Calculate menu position from adjusted range. x is the LEFT edge of
        // the selection (the menu clamps itself to stay in the viewport).
        const rect = adjustedRange.getBoundingClientRect();
        const position = {
          x: rect.left,
          y: rect.top - 10, // Show menu above selection (positive y = show above)
        };
        setMenuPosition(position);
      } else {
        // Clear selection if nothing is selected after adjustment
        setSelectedText('');
        setSelection(null);
        setMenuPosition(null);
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [pageNumber]); // Add pageNumber as dependency so we capture current page

  // Helper function to expand selection to word boundaries
  const expandToWordBoundaries = (range: Range): Range => {
    const newRange = range.cloneRange();

    // Get start and end containers and offsets
    const startContainer = newRange.startContainer;
    const endContainer = newRange.endContainer;
    let startOffset = newRange.startOffset;
    let endOffset = newRange.endOffset;

    // Expand start to word boundary
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const textContent = startContainer.textContent || '';

      // Move backward to find word boundary (space, newline, or start of text)
      while (startOffset > 0 && /\S/.test(textContent[startOffset - 1])) {
        startOffset--;
      }

      newRange.setStart(startContainer, startOffset);
    }

    // Expand end to word boundary
    if (endContainer.nodeType === Node.TEXT_NODE) {
      const textContent = endContainer.textContent || '';

      // Move forward to find word boundary (space, newline, or end of text)
      while (endOffset < textContent.length && /\S/.test(textContent[endOffset])) {
        endOffset++;
      }

      newRange.setEnd(endContainer, endOffset);
    }

    return newRange;
  };

  // Clear selection handler
  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelectedText('');
    setSelection(null);
    setMenuPosition(null);
  }, []);

  // Handle creating highlight from selection
  const handleCreateHighlight = useCallback(
    async (color: HighlightColor, noteText?: string) => {
      if (!selectedText) {
        console.warn('⚠️ No text selected for highlight');
        return;
      }

      // Validate page number
      if (selectionPageNumber < 1 || selectionPageNumber > numPages) {
        console.error('❌ INVALID PAGE NUMBER:', selectionPageNumber, 'Total pages:', numPages);
        showToast(`Invalid page number: ${selectionPageNumber}. Please try selecting the text again.`, { type: 'warning' });
        return;
      }

      console.log('🎨 CREATING HIGHLIGHT:', {
        selectedText: selectedText.substring(0, 80) + (selectedText.length > 80 ? '...' : ''),
        color,
        pageNumber: selectionPageNumber, // Use page where selection was made
        currentPage: pageNumber, // For comparison
        totalPages: numPages,
        noteText: noteText || '(none)'
      });

      // Warn if page numbers don't match (user navigated away)
      if (selectionPageNumber !== pageNumber) {
        console.warn('⚠️ SELECTION PAGE ≠ CURRENT PAGE:', {
          selectionPage: selectionPageNumber,
          currentPage: pageNumber,
          message: 'User may have navigated away from selection page'
        });
      }

      try {
        const result = await createHighlight({
          location: selectionPageNumber, // Use page where text was selected, not current page
          locationLabel: `Page ${selectionPageNumber}`,
          selectedText,
          color,
          noteText,
        });

        console.log('✅ HIGHLIGHT CREATED SUCCESSFULLY:', {
          highlightId: result?.id,
          page: selectionPageNumber,
          text: selectedText.substring(0, 50) + '...'
        });
        clearSelection();

        // Show success feedback
        // The highlight will appear in the Notes sidebar
      } catch (error) {
        console.error('❌ Failed to create highlight:', error);
        showToast(`Failed to create highlight: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
      }
    },
    [selectedText, selectionPageNumber, pageNumber, numPages, createHighlight, clearSelection]
  );

  // Handle creating flashcard from selection
  const handleCreateFlashcard = useCallback(
    async (question: string, answer: string, sourceText: string) => {
      try {
        // Ensure we have a deck
        let deckId = currentDeckId;
        if (!deckId) {
          // Create default deck for this book
          const defaultDeck = await createDeck({
            bookId,
            name: `${metadata?.title || 'Book'} Flashcards`,
            description: 'Flashcards created from PDF highlights',
          });
          deckId = defaultDeck.id;
          setCurrentDeckId(deckId);
        }

        const locationLabel = `Page ${selectionPageNumber}`;

        await createFlashcard({
          deckId,
          question,
          answer,
          location: selectionPageNumber, // Use page where text was selected
          locationLabel,
          sourceText,
        });

        clearSelection();
      } catch (error) {
        console.error('Failed to create flashcard:', error);
        showToast('Failed to create flashcard. Please try again.', { type: 'error' });
      }
    },
    [currentDeckId, bookId, metadata, selectionPageNumber, createDeck, createFlashcard, setCurrentDeckId, clearSelection]
  );

  // Apply highlights to react-pdf text layer using Range API for character-level precision
  const applyHighlightsToTextLayer = useCallback(() => {
    console.log(`\n📝 [HIGHLIGHT RENDER] Starting to apply highlights. Total notes: ${notes.length}, Current page: ${pageNumber}`);

    // react-pdf uses .react-pdf__Page__textContent for the text layer
    const textLayer = document.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) {
      console.log('⏳ [HIGHLIGHT RENDER] Text layer not found yet, will retry...');
      return;
    }

    // Get highlights for current page
    const pageHighlights = notes.filter(note => {
      const noteLocation = typeof note.location === 'number' ? note.location : parseInt(String(note.location), 10);
      const matches = noteLocation === pageNumber;
      return matches;
    });

    console.log(`📊 [HIGHLIGHT RENDER] Found ${pageHighlights.length} highlights for page ${pageNumber}:`);
    if (pageHighlights.length > 0) {
      pageHighlights.forEach((h, idx) => {
        console.log(`  ${idx + 1}. "${h.selectedText?.substring(0, 60)}..." [${h.color}] on page ${h.location}`);
      });
    }

    if (pageHighlights.length === 0) {
      console.log(`✅ [HIGHLIGHT RENDER] No highlights for page ${pageNumber} - nothing to render`);
      return;
    }

    // Get all text nodes in the text layer
    const walker = document.createTreeWalker(
      textLayer,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      // Only include non-empty text nodes
      if (textNode.textContent && textNode.textContent.length > 0) {
        textNodes.push(textNode);
      }
    }

    if (textNodes.length === 0) {
      console.warn('No text nodes found in PDF text layer');
      return;
    }

    // Build a full text string with node position tracking
    let fullText = '';
    const nodePositions: Array<{ node: Text; start: number; end: number }> = [];

    textNodes.forEach(textNode => {
      const start = fullText.length;
      const text = textNode.textContent || '';
      fullText += text;
      const end = fullText.length;
      nodePositions.push({ node: textNode, start, end });
    });

    // Sort highlights by position (reverse order - end to start)
    // This ensures that earlier highlights don't invalidate later positions
    const sortedHighlights = [...pageHighlights].sort((a, b) => {
      const aIndex = fullText.indexOf(a.selectedText || '');
      const bIndex = fullText.indexOf(b.selectedText || '');
      return bIndex - aIndex; // Reverse order
    });

    // Apply each highlight using Range API (in reverse order)
    sortedHighlights.forEach((highlight, idx) => {
      const searchText = highlight.selectedText;
      if (!searchText) {
        console.warn(`⚠️ Highlight ${idx + 1} has no selectedText, skipping`);
        return;
      }

      console.log(`\n🔍 [TEXT MATCH] Attempting to find highlight ${idx + 1}/${sortedHighlights.length}:`);
      console.log(`   Text: "${searchText.substring(0, 80)}${searchText.length > 80 ? '...' : ''}"`);
      console.log(`   Saved page: ${highlight.location}, Current page: ${pageNumber}`);

      // Try multiple matching strategies in order of preference
      let startIndex = -1;
      let matchStrategy = '';

      // Strategy 1: Exact match
      startIndex = fullText.indexOf(searchText);
      if (startIndex !== -1) {
        matchStrategy = 'exact match';
        console.log(`   ✅ Exact match found at position: ${startIndex}`);
      }

      // Strategy 2: Normalized whitespace
      if (startIndex === -1) {
        console.log('   ⚠️ Exact match failed, trying normalized whitespace...');
        const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();
        const normalizedSearchText = normalizeText(searchText);
        const normalizedFullText = normalizeText(fullText);

        const normalizedIndex = normalizedFullText.indexOf(normalizedSearchText);
        if (normalizedIndex !== -1) {
          // Map back to original position
          const wordsBeforeMatch = normalizedFullText.substring(0, normalizedIndex).split(/\s+/).filter(w => w.length > 0).length;
          let wordCount = 0;
          let charPos = 0;

          for (let i = 0; i < fullText.length && wordCount < wordsBeforeMatch; i++) {
            if (fullText[i].match(/\S/) && (i === 0 || fullText[i-1].match(/\s/))) {
              wordCount++;
            }
            if (wordCount < wordsBeforeMatch) {
              charPos = i + 1;
            }
          }

          startIndex = charPos;
          matchStrategy = 'normalized whitespace';
          console.log(`   ✅ Found via normalized whitespace at position: ${startIndex}`);
        }
      }

      // Strategy 3: Case-insensitive match
      if (startIndex === -1) {
        console.log('   ⚠️ Normalized match failed, trying case-insensitive...');
        const lowerSearchText = searchText.toLowerCase();
        const lowerFullText = fullText.toLowerCase();
        const caseInsensitiveIndex = lowerFullText.indexOf(lowerSearchText);

        if (caseInsensitiveIndex !== -1) {
          startIndex = caseInsensitiveIndex;
          matchStrategy = 'case-insensitive';
          console.log(`   ✅ Found via case-insensitive match at position: ${startIndex}`);
        }
      }

      // Strategy 4: Substring match (find partial match of at least 50% of the text)
      if (startIndex === -1 && searchText.length > 20) {
        console.log('   ⚠️ Case-insensitive failed, trying substring match (50% minimum)...');

        // Try to find the longest substring that exists in fullText
        const minLength = Math.floor(searchText.length * 0.5);

        for (let len = searchText.length; len >= minLength; len--) {
          const substring = searchText.substring(0, len);
          const substringIndex = fullText.indexOf(substring);

          if (substringIndex !== -1) {
            startIndex = substringIndex;
            matchStrategy = `substring match (${Math.round((len / searchText.length) * 100)}% of text)`;
            console.log(`   ✅ Found ${len} chars (${Math.round((len / searchText.length) * 100)}%) at position: ${startIndex}`);
            break;
          }
        }
      }

      // Strategy 5: Try trimming leading characters (handle "n my tenth" → "In my tenth")
      if (startIndex === -1 && searchText.length > 5) {
        console.log('   ⚠️ Substring failed, trying with leading char variations...');

        // Try adding common leading characters
        const leadingVariations = ['I' + searchText, ' ' + searchText, searchText.substring(1)];

        for (const variation of leadingVariations) {
          const varIndex = fullText.indexOf(variation);
          if (varIndex !== -1) {
            // Adjust startIndex to point to where the original searchText would be
            if (variation === searchText.substring(1)) {
              // We removed first char, so match is one char before
              startIndex = varIndex - 1;
            } else {
              // We added a char, so match is one char after
              startIndex = varIndex + 1;
            }
            matchStrategy = `leading char variation ("${variation.substring(0, 20)}...")`;
            console.log(`   ✅ Found via leading variation "${variation.substring(0, 10)}..." at position: ${startIndex}`);
            break;
          }
        }
      }

      if (startIndex === -1) {
        console.error(`\n❌ [TEXT MATCH FAILED] Could not find highlight text on page ${pageNumber}`);
        console.error(`   Highlight ID: ${highlight.id}`);
        console.error(`   Saved page: ${highlight.location}`);
        console.error(`   Search text: "${searchText.substring(0, 100)}..."`);
        console.error(`   Page full text (first 500 chars): "${fullText.substring(0, 500)}..."`);
        console.error(`   All 5 matching strategies failed. This highlight will NOT be rendered.`);
        return;
      }

      // Calculate end index
      const endIndex = startIndex + searchText.length;

      console.log(`   📍 Match strategy: ${matchStrategy}`);
      console.log(`   📍 Highlighting text at indices ${startIndex}-${endIndex} in fullText (length: ${fullText.length})`);

      try {
        // Find the text nodes that contain this range
        const startNode = nodePositions.find(np => np.start <= startIndex && np.end > startIndex);
        const endNode = nodePositions.find(np => np.start < endIndex && np.end >= endIndex);

        if (!startNode || !endNode) {
          console.warn('Could not find text nodes for highlight');
          return;
        }

        // Calculate offsets within the text nodes
        const startOffset = startIndex - startNode.start;
        const endOffset = endIndex - endNode.start;

        console.log(`Start node: range [${startNode.start}-${startNode.end}], offset: ${startOffset}, text: "${startNode.node.textContent?.substring(0, 30)}"`);
        console.log(`End node: range [${endNode.start}-${endNode.end}], offset: ${endOffset}, text: "${endNode.node.textContent?.substring(0, 30)}"`);

        // Validate offsets are within node bounds
        const startNodeLength = startNode.node.textContent?.length || 0;
        const endNodeLength = endNode.node.textContent?.length || 0;

        if (startOffset < 0 || startOffset > startNodeLength) {
          console.warn(`Invalid start offset: ${startOffset} for node length ${startNodeLength}, node text: "${startNode.node.textContent}"`);
          return;
        }

        if (endOffset < 0 || endOffset > endNodeLength) {
          console.warn(`Invalid end offset: ${endOffset} for node length ${endNodeLength}, node text: "${endNode.node.textContent}"`);
          return;
        }

        // NEW APPROACH: Apply highlight directly to react-pdf's existing spans
        // instead of wrapping/extracting (which breaks absolute positioning)

        const bgColor = getTransparentHighlightColor(highlight.color);
        console.log(`   🎨 Applying highlight color: ${bgColor}`);

        // Find all react-pdf spans that overlap with this highlight range
        const affectedNodes = nodePositions.filter(
          np => !(np.end <= startIndex || np.start >= endIndex)
        );

        console.log(`   📍 Highlighting ${affectedNodes.length} text node(s)`);

        affectedNodes.forEach((nodePos, idx) => {
          // Find the parent span element (react-pdf's absolutely positioned span)
          let parentSpan = nodePos.node.parentElement;

          if (parentSpan && parentSpan.tagName === 'SPAN') {
            // Apply highlight styles directly to the existing react-pdf span
            parentSpan.style.backgroundColor = bgColor;
            parentSpan.style.cursor = 'pointer';
            parentSpan.classList.add('pdf-highlight');
            parentSpan.setAttribute('data-highlight-id', highlight.id);

            // Add click handler
            const clickHandler = () => setClickedHighlight(highlight);
            parentSpan.onclick = clickHandler;

            console.log(`     ${idx + 1}. Applied to span: "${nodePos.node.textContent?.substring(0, 40)}..."`);
          }
        });

        console.log(`   ✅ Applied highlight to ${affectedNodes.length} existing span(s) (ID: ${highlight.id})`);

        // Verify one of the spans
        setTimeout(() => {
          const verifySpan = document.querySelector(`[data-highlight-id="${highlight.id}"]`);
          if (verifySpan) {
            const computedStyle = window.getComputedStyle(verifySpan);
            console.log(`   🔍 DOM VERIFICATION (${highlight.id}):`, {
              inDOM: true,
              className: verifySpan.className,
              backgroundColor: computedStyle.backgroundColor,
              position: computedStyle.position,
              display: computedStyle.display,
              top: computedStyle.top,
              left: computedStyle.left,
              textContent: verifySpan.textContent?.substring(0, 50) + '...'
            });
          } else {
            console.error(`   ❌ DOM VERIFICATION FAILED: No spans found with highlight ID`);
          }
        }, 100);
      } catch (error) {
        console.error('Failed to apply highlight:', error);
      }
    });
  }, [notes, pageNumber]);

  // Apply highlights when page changes or notes change
  // Wait for react-pdf text layer to render, then apply highlights
  useEffect(() => {
    // Use a small delay to ensure text layer is fully rendered
    const timer = setTimeout(() => {
      applyHighlightsToTextLayer();
    }, 100);

    return () => clearTimeout(timer);
  }, [pageNumber, notes, applyHighlightsToTextLayer]);

  // Convert metadata to BookMetadata format
  const bookMetadata: BookMetadata | null = metadata
    ? {
        id: metadata.id,
        title: metadata.title,
        author: metadata.author,
        publisher: metadata.publisher,
        isbn: metadata.isbn,
        coverImageUrl: metadata.coverImageUrl,
        description: metadata.description,
        publishedDate: metadata.publishedDate,
        pageCount: metadata.pageCount,
        language: metadata.language,
        format: BookFormat.Pdf,
      }
    : null;

  if (isLoading || !bookMetadata) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-800">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-lg text-white">Loading book...</span>
      </div>
    );
  }

  return (
    <UnifiedReaderLayout
      bookId={bookId}
      format={BookFormat.Pdf}
      metadata={bookMetadata}
      navigation={navigation}
      navigationCallbacks={navigationCallbacks}
      tableOfContents={tableOfContents}
      bookmarks={bookmarks}
      notes={notes}
      actions={actions}
      onAddPageNote={handleAddPageNote}
      activeSecureContentChunkId={activeSecureContentChunkId}
      zoomControls={{
        zoomLevel: Math.round(userZoom * 100),
        onZoomIn: handleZoomIn,
        onZoomOut: handleZoomOut,
        onResetZoom: handleResetZoom,
      }}
    >
      {/* PDF Renderer using react-pdf with HTTP Range request support */}
      <div
        ref={pdfContainerRef}
        style={{
          flex: '1 1 auto',
          alignSelf: 'stretch',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto',
          padding: '1rem',
          boxSizing: 'border-box',
        }}
        className="bg-neutral-800"
      >
        <Document
          file={pdfFileConfig}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-lg text-white">Loading PDF...</span>
            </div>
          }
          error={
            <div className="text-center py-20 text-white">
              <p className="text-xl font-semibold mb-2">Failed to load PDF document.</p>
              <p className="text-sm text-neutral-400">Please check your connection and try again.</p>
            </div>
          }
          options={pdfOptions}
        >
          <Page
            pageNumber={pageNumber}
            height={pagePixelHeight}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            customTextRenderer={customTextRenderer}
            onRenderTextLayerSuccess={handleTextLayerRendered}
            loading={
              <div className="flex items-center justify-center py-20">
                <LoadingSpinner size="md" />
              </div>
            }
            className="shadow-2xl"
          />
        </Document>
      </div>

      {/* Text Selection Menu for creating highlights and flashcards */}
      {selectedText && menuPosition && (
        <TextSelectionMenuWithFlashcard
          selectedText={selectedText}
          selection={selection}
          menuPosition={menuPosition}
          bookMetadata={bookMetadata}
          locationLabel={`Page ${selectionPageNumber ?? pageNumber}`}
          onCreateHighlight={handleCreateHighlight}
          onCreateFlashcard={handleCreateFlashcard}
          onClose={clearSelection}
        />
      )}

      {/* Highlight Note Modal - shows when clicking on a highlight */}
      {clickedHighlight && (
        <HighlightNoteModal
          note={clickedHighlight}
          bookMetadata={bookMetadata}
          onClose={() => setClickedHighlight(null)}
          onUpdate={async (id: string, color: HighlightColor, noteText?: string) => {
            await updateHighlight({
              id,
              color,
              noteText,
            });
            setClickedHighlight(null);
          }}
          onDelete={async () => {
            await deleteHighlight(clickedHighlight.id);
            setClickedHighlight(null);
          }}
        />
      )}
    </UnifiedReaderLayout>
  );
}

/**
 * Unified PDF Renderer - DRM-aware wrapper
 *
 * This component automatically detects DRM status and wraps the reader
 * in SecureReadingProvider when DRM is enabled. For non-DRM books,
 * it renders the reader directly without DRM overhead.
 *
 * Features (available for ALL books):
 * - Table of Contents navigation
 * - Highlights with color picker (text selection always enabled)
 * - Notes and annotations
 * - Bookmarks
 * - Text selection and highlighting
 * - Search functionality
 * - Flashcard creation from selected text
 * - Progressive chunk loading (5MB chunks for DRM, 5-page chunks for non-DRM)
 *
 * DRM Features (only when isDrmEnabled):
 * - Encrypted chunk delivery via SecureReadingContext
 * - DRM quota tracking and display
 * - Content protection (no right-click, no Ctrl+C, no print)
 * - DevTools detection and session termination
 * - Session management with heartbeat
 * - Copy prevention via keyboard shortcuts (allows highlighting but blocks copying)
 */
export default function UnifiedPdfRenderer({ bookId, isDrmEnabled = false }: UnifiedPdfRendererProps) {
  // If DRM is enabled, wrap in SecureReadingProvider
  if (isDrmEnabled) {
    return (
      <SecureReadingProvider>
        <UnifiedPdfRendererInternal bookId={bookId} isDrmEnabled={true} />
      </SecureReadingProvider>
    );
  }

  // Non-DRM books render directly
  return <UnifiedPdfRendererInternal bookId={bookId} isDrmEnabled={false} />;
}
